import {useMemo} from 'react';

import {
    addManyToSelection,
    addToSelection,
    clearSelection,
    removeFromSelection,
} from '../reducers';
import {selectionSelector} from '../selectors';

import {
    useAppDispatch,
    useAppSelector,
} from '../../../hooks/useTypedReactRedux';

/**
 * FNP-918 / FNP-951: row selection for the payment-intents table.
 *
 * Selection lives in the Redux `payment-intents.selection` slice, per
 * MODULE_FEATURE_CHECKLIST §3. It previously lived as `useState<PaymentIntent[]>`
 * in IndexPage and was threaded down through RecordsTable as two props
 * (`selectedRecords` + `setSelectedRecords`), which meant a sibling of the
 * table — the delete button in the page header — could only see selection if
 * the page manually relayed it.
 *
 * The slice stores the whole record keyed by id. Consumers here read
 * `selection[id]` as a BOOLEAN, so the boolean map is derived below rather than
 * returned raw — handing back the record map would make an `=== true` check
 * false for every genuinely-selected row.
 *
 * ⚠️ The store is app-scoped, so selection SURVIVES unmount and route changes.
 * That is the point (a sibling outside this hook's owner can read it), but it
 * means anything that finishes a batch action must clear explicitly — IndexPage
 * passes handleClearSelection as onDeleteComplete for exactly that. On a
 * money-path module that matters more than most: a stale id left selected after
 * a cancel would put a record that no longer exists into the next confirm.
 * It also means tests need a fresh store per render; see tests/harness.tsx.
 */
export interface UsePaymentIntentsSelectionResult {
    /** id -> true for every selected row. */
    selection: Record<string, boolean>;
    selectedIds: string[];
    handleRowSelection: (id: string, checked: boolean) => void;
    handleSelectAllRows: (checked: boolean, items: {id: string}[]) => void;
    handleClearSelection: () => void;
}

export const usePaymentIntentsSelection = (): UsePaymentIntentsSelectionResult => {
    const dispatch = useAppDispatch();

    const selected = useAppSelector(selectionSelector);

    const selectedIds = useMemo(() => Object.keys(selected), [selected]);

    const selection: Record<string, boolean> = useMemo(
        () => selectedIds.reduce<Record<string, boolean>>(
            (acc, id) => ({
                ...acc,
                [id]: true,
            }),
            {},
        ),
        [selectedIds],
    );

    const handleRowSelection = (id: string, checked: boolean) => {
        if (checked) {
            dispatch(addToSelection({id}));
        } else {
            dispatch(removeFromSelection(id));
        }
    };

    const handleSelectAllRows = (checked: boolean, items: {id: string}[]) => {
        if (checked) {
            // One dispatch for the whole page rather than one per row — see
            // addManyToSelection in SelectionSliceFor.
            dispatch(addManyToSelection(items.map(item => ({id: item.id}))));
        } else {
            dispatch(clearSelection());
        }
    };

    const handleClearSelection = () => {
        dispatch(clearSelection());
    };

    return {
        selection,
        selectedIds,
        handleRowSelection,
        handleSelectAllRows,
        handleClearSelection,
    };
};
